import React, { useState } from "react";
import Markdown from "react-markdown";

const CaseStudies = ({ classicHeader, darkTheme }) => {
  const [expandedStudy, setExpandedStudy] = useState(null);

  // Case study data - you could also import these from separate files
  const caseStudies = [
    {
      id: 1,
      title: "Revitalizing a Gridlocked Interior Design Platform",
      summary:
        "How I transformed a technically gridlocked platform with chaotic infrastructure into a reliable system with predictable development cycles, leading to significant business growth and successful seed funding.",
      content: `# Case Study: Revitalizing a Gridlocked Interior Design Platform

## The Challenge

An exclusive interior design platform connecting clients with high-end designers was facing critical technical challenges that threatened its growth. The application had been built haphazardly by a series of freelancers, resulting in:

- A chaotic infrastructure spread across multiple providers
- Inconsistent deployment methods
- A fragmented codebase mixing create-react-app and Next.js
- Numerous security vulnerabilities
- Development gridlock with no releases for months
- Broken staging and QA environments making testing nearly impossible
- An imminent transition from the existing development team with risk of knowledge loss

The founder had lost confidence in the current team and needed a complete technical overhaul to move forward.

## The Approach

As Fractional CTO, I implemented a strategic plan to stabilize the platform and create a foundation for growth:

1. **Knowledge Transfer Management**
   - Conducted intensive knowledge-gathering sessions with the outgoing lead developer
   - Documented all existing processes, infrastructure access, and deployment procedures
   - Created comprehensive documentation where gaps existed

2. **Infrastructure Consolidation**
   - Migrated all services to Vercel for a unified infrastructure approach
   - Provided all stakeholders with appropriate access levels to branch deployments and staging environments
   - Implemented proper environment separation for development, testing, and production

3. **Team Restructuring**
   - Assembled a new LATAM-based development team with two developers and one QA resource distributed across Argentina, Uruguay, and Bolivia
   - Implemented lightweight Agile practices appropriate for a small team ("agile with a lowercase 'a'")
   - Established rigorous code review processes, multi-stage QA, and detailed release notes

4. **Technical Improvements**
   - Normalized dependency versions across all applications
   - Optimized asset serving, particularly for high-resolution images critical to the platform
   - Implemented coding best practices to improve site performance
   - Enhanced security measures to address vulnerabilities

5. **Process Implementation**
   - Created metrics tracking for QA cycles per feature and development-to-production timelines
   - Established a culture of accountability and collaboration
   - Implemented "bottleneck swarming" to prevent individual team members from getting stuck

## The Results

Over the course of approximately one year, the transformation delivered significant measurable improvements:

### Technical Achievements
- **Deployment Frequency**: From almost no releases to a stable twice-weekly release cycle
- **Path to Continuous Delivery**: Progressed from chaotic manual deployments to evaluating a CD strategy with comprehensive testing in the CI pipeline
- **Performance**: Dramatically improved site speed through asset optimization and coding best practices
- **SEO**: Achieved significant SEO gains through technical improvements
- **Stability**: Reduced load times and delivered more stable user sessions

### Business Impact
- **Predictable Development**: Established confidence in feature and fix delivery timelines
- **Team Alignment**: Created a cohesive development process with clear communication
- **User Base Growth**: Considerably expanded the paying user base
- **User Satisfaction**: Improved application user satisfaction while reducing bugs
- **Funding Success**: Contributed to the company's ability to close its first seed funding round

### Key Milestones
1. Successful knowledge transition and technical ownership
2. Strategic team assembly with the right skill sets
3. Redesign launch with improved performance
4. Establishment of reliable release cycles
5. Implementation of new verticals and subscription growth

## Conclusion

This transformation demonstrates how the right technical leadership can turn around a struggling application through a combination of strategic infrastructure decisions, team building, and process implementation. By focusing on both technical excellence and business outcomes, the platform was able to overcome development gridlock and establish a foundation for sustainable growth and investor confidence.`,
    },
    {
      id: 2,
      title: "Launching a Compliance-First Victim Services Platform in Under 90 Days",
      summary:
        "How I took a multitenant, fully audited case management platform for victim service providers from zero to production — team, code, a survivor-facing mobile app with disguise mode, and HIPAA compliance — in under 90 days, then earned HUD certification and displaced three incumbent vendors in the first year.",
      content: `# Case Study: Launching a Compliance-First Victim Services Platform in Under 90 Days
    
    ## The Challenge
    
    A newly formed company set out to build a case management platform for victim service providers — domestic violence shelters, sexual assault response programs, and advocacy organizations. These organizations operate under some of the most demanding data handling requirements of any sector. They hold protected health information, they coordinate with law enforcement, and they are bound by confidentiality rules where an accidental disclosure is not a compliance footnote but a threat to a survivor's physical safety.
    
    There was no product, no team, and no infrastructure. What existed was a market opportunity and a very short window to capture it:
    
    - An incumbent landscape of legacy platforms with dated interfaces, long implementation cycles, and pricing that strained nonprofit budgets
    - Prospective clients already running on three different competing systems, each holding years of case history, service records, and attached documentation
    - Overlapping regulatory obligations — HIPAA, CJIS, HUD/HMIS data standards, and VAWA confidentiality requirements — that had to be satisfied from the first line of code rather than retrofitted after launch
    - Two entirely different user populations to serve: front-line advocates managing case loads, and survivors themselves, many of whom cannot safely be seen using a victim services application on their own phone
    - Grant-funded organizations whose reporting obligations to funders are as operationally critical as the case work itself
    - A launch timeline measured in weeks, because the first committed clients could not stay on their existing contracts indefinitely
    
    The mandate was not to improve an existing system. It was to stand one up — team, architecture, product, and compliance posture — fast enough to be credible to organizations that could not afford a failed migration.
    
    ## The Approach
    
    As Fractional CTO, I owned the technical organization end to end: hiring, architecture, delivery, compliance, and client onboarding.
    
    1. **Hiring Ahead of the Code**
       - Defined the engineering roles required for a compliance-heavy, multi-surface product before writing production code
       - Recruited and onboarded a small senior team capable of operating without heavy management overhead
       - Structured the team so that mobile, backend, and infrastructure work could proceed in parallel from week one
       - Established review, QA, and release practices immediately, rather than treating them as post-launch maturity work
    
    2. **Compliance as an Architectural Constraint**
       - Designed for HIPAA compliance from day one — encryption in transit and at rest, least-privilege access, tenant isolation, and documented data handling procedures built into the initial architecture
       - Implemented multitenancy with strict data separation, so that no client organization's records could be reachable from another's context
       - Built role-based access control aligned to how advocacy organizations actually operate: advocates, supervisors, program directors, and administrators with materially different visibility into client records
       - Treated confidentiality requirements as product requirements, including consent-aware data sharing and controls over what leaves the platform
    
    3. **A Fully Audited System of Record**
       - Made the audit trail a first-class feature rather than a logging afterthought: every access, modification, and export of sensitive data is recorded and attributable
       - Designed the audit model to support both compliance evidence and operational investigation, so organizations can answer "who saw this record, and when" without engineering involvement
       - Planned retention and archival strategy up front, keeping the audit history queryable without degrading day-to-day performance
    
    4. **Infrastructure Matched to Nonprofit Economics**
       - Built on a serverless architecture that scales with actual usage, avoiding the fixed infrastructure cost that forces incumbent vendors into high per-seat pricing
       - Established separate development, staging, and production environments from the outset, with branch-level deployments enabling real review before release
       - Instrumented monitoring, alerting, and incident response early, so reliability was measurable rather than assumed
    
    5. **A Survivor-Facing Mobile App Shipped Alongside Version One**
       - Delivered native applications to both the App Store and Google Play as part of the initial launch, not as a later phase
       - Built the mobile experience for survivors rather than staff, giving clients a direct and private channel to their own case, their advocate, and safety resources
       - Designed against a threat model most consumer software never has to consider: the person most likely to pick up the phone is not the person who owns it. Abusers routinely have physical access to a survivor's device.
       - Implemented a disguise mode so the application does not present itself as a victim services tool, along with the surrounding behaviors that make that meaningful rather than cosmetic — discreet notification handling and a fast, unremarkable exit from the app
       - Absorbed the additional overhead of two app store review processes into the same sub-90-day window
    
    6. **HMIS Certification as a Fast Follow**
       - Implemented the HUD HMIS data standards — universal data elements, program descriptor data, and the required export and reporting specifications
       - Managed the certification process directly, achieving full HUD certification in under 60 days
       - Made federal reporting a native capability rather than a manual, spreadsheet-driven exercise for grant-funded organizations
    
    7. **A Repeatable Migration Playbook**
       - Built extraction and transformation tooling for each competing platform, mapping foreign schemas onto the new data model
       - Migrated complete case loads — client records, service history, case notes, and attached documentation — rather than a partial snapshot
       - Ran parallel validation before cutover so organizations could verify their own data before committing
       - Sequenced cutovers to minimize disruption, keeping advocates in service delivery instead of data reconciliation
    
    ## The Results
    
    ### Delivery
    - **Zero to Production in Under 90 Days**: A multitenant, fully audited platform plus a survivor-facing iOS and Android application, delivered from a standing start — including assembling the team
    - **HUD Certification in Under 60 Days**: Full HMIS implementation and certification, opening access to federally funded programs
    - **HIPAA Compliant from Day One**: No remediation phase, no retrofit, no compliance debt carried into the first client engagement
    - **Three Platform Migrations**: Complete case load transfers from three separate competing systems with minimal interruption to client services
    
    ### Technical Achievements
    - **Tenant Isolation at Scale**: Multitenant architecture supporting organizations with materially different programs, workflows, and reporting obligations
    - **Complete Auditability**: End-to-end audit coverage of sensitive data access, designed to satisfy both regulators and internal investigation needs
    - **Elastic Infrastructure**: Serverless deployment model that absorbs variable load without idle capacity cost
    - **Extensible Compliance Posture**: The same architectural decisions that delivered HIPAA compliance from day one absorbed law enforcement data handling requirements without re-platforming
    - **Privacy Under Adversarial Conditions**: A survivor-facing mobile application with disguise mode, built for the reality that the device itself may be monitored
    
    ### Business Impact
    - **Six Clients in the First Year**: A new entrant winning against established vendors in a conservative, reference-driven market
    - **Incumbent Displacement**: Every migrated client left a competing platform, validating both the product and the migration process as competitive advantages
    - **Continuity of Services**: Organizations transitioned without pausing case work, protecting the people they serve during the change
    - **Funder Reporting Built In**: HMIS certification and native reporting removed a recurring administrative burden from grant-funded staff
    - **Accessible Pricing**: Infrastructure economics allowed the platform to be priced within reach of the nonprofits it serves
    
    ### Key Milestones
    1. Team assembled and technical foundation established
    2. Multitenant, HIPAA-compliant platform live in production
    3. Mobile applications approved and published to both app stores
    4. First competing-platform migration completed with full case load transfer
    5. HUD HMIS certification achieved
    6. Six client organizations onboarded within the first year
    
    ## Key Insights
    
    1. **Compliance Is Cheapest at the Beginning**: Building HIPAA and audit requirements into the initial architecture cost weeks. Retrofitting them into a live system holding sensitive records would have cost quarters — and would have required asking clients to trust a system that had already handled their data incorrectly.
    
    2. **Migration Is the Product**: In a market where every prospective client is already on something else, the ability to move a complete case load reliably is not a professional services line item. It is the feature that closes the deal.
    
    3. **Speed and Rigor Are Not Opposites**: The same decisions that made the platform auditable — explicit data models, clear access boundaries, strong environment separation — also made it faster to build on. Discipline was the accelerant, not the tax.
    
    4. **Design for the Threat Model You Actually Have**: For most software, security means keeping attackers away from the server. Here the likeliest adversary is a person holding the survivor's phone. Building disguise mode into the first release — rather than waiting for a client to ask for it — meant survivor privacy shaped the product instead of being patched onto it.
    
    5. **Small Senior Teams Move Fastest**: A compressed timeline with high compliance stakes is not the environment for a large team and heavy coordination. Fewer, more capable engineers with clear ownership delivered more in 90 days than a conventional build would have in a year.
    
    ## Conclusion
    
    This engagement demonstrates what technical leadership looks like at the zero-to-one stage, in a domain where the cost of getting it wrong is measured in human safety rather than downtime. By treating compliance, auditability, survivor privacy, and migration capability as core architecture rather than downstream obligations, the platform launched fast, certified quickly, and won clients directly from established competitors — while giving the organizations it serves a system they could trust with the most sensitive records they hold, and giving survivors a way to reach help without putting themselves at risk to do it.`,
    },
    {
      id: 3,
      title: "Reimagining a Healthcare Professional Network Platform",
      summary:
        "How I transformed an outdated healthcare professional network platform with accumulated technical debt into a reliable system with near-zero bugs and 100% uptime, enabling unprecedented business growth.",
      content: `# Case Study: Revitalizing a Healthcare Professional Network Platform

## The Challenge

A SaaS platform connecting life sciences companies with healthcare professionals (doctors, nurses, and other medical experts) was struggling with significant technical challenges that were hindering growth. Despite being acquired a year earlier, the company was facing:

- A technically outdated platform with years of accumulated technical debt
- Neglected dependency updates that weren't prioritized by the previous outsourced development team
- Frequent regressions when launching new features
- Communication barriers between product management and the development team
- Opaque outsourced development structure with inconsistent team members
- Limited resources dedicated to maintenance, yet insufficient focus on growth initiatives
- Infrastructure and deployment processes that caused interruptions to users

The company found itself in a paradoxical situation: needing to build for growth but unable to move forward due to the constant effort required just to keep the existing platform operational.

## The Approach

As Fractional CTO, I implemented a strategic plan focused on stability first, followed by sustainable growth:

1. **Immediate Stabilization**
   - Paused all new feature development to focus on critical system needs
   - Identified and addressed the most urgent issues impacting reliability
   - Initiated comprehensive dependency updates across the technology stack

2. **Team Restructuring**
   - Replaced the opaque outsourced team with direct leadership
   - Initially handled development personally to stabilize the platform
   - Strategically added a senior frontend engineer and later a mid-level backend engineer

3. **Infrastructure Modernization**
   - Migrated from self-hosted GitLab and Circle CI to GitHub and GitHub Actions
   - Deployed the backend to a new, better-hardened EC2 instance
   - Moved the frontend application to Vercel for improved reliability
   - Implemented multiple environment tiers to enable parallel development and user testing

4. **Technical Debt Resolution**
   - Updated both Laravel (backend) and Angular (frontend) to current versions
   - Made necessary code adaptations to support modern dependencies
   - Established consistent patterns for backend development

5. **Complete Application Rewrite**
   - Applied lessons learned from the original application
   - Rebuilt the platform with a focus on user workflows and automation
   - Maintained the same technology choices (Laravel and Angular) to leverage existing expertise

## The Results

The transformation delivered significant measurable improvements across multiple dimensions:

### Technical Achievements
- **Reliability**: Reduced production bugs to nearly zero
- **Uptime**: Achieved nearly 100% platform availability
- **Deployment**: Established predictable, non-disruptive deployment cycles
- **Development Process**: Implemented structured roadmaps with weekly progress demos

### Business Impact
- **Employee Productivity**: Eliminated dozens of employee hours spent on menial tasks monthly
- **Network Growth**: Increased onboarding capacity by several times
- **User Satisfaction**: Received positive feedback about improved daily work experience
- **Strategic Capabilities**: Enabled new business initiatives previously impossible to implement
- **External Expansion**: Successfully opened the platform to more external companies
- **Unprecedented Growth**: Achieved record growth rates after platform stabilization

### Process Improvements
- **Predictable Development**: Established reliable weekly cycles with consistent delivery
- **Transparent Communication**: Implemented company-wide updates on progress and launch dates
- **User-Centric Design**: Integrated empathy into the product development process
- **Automated Onboarding**: Transformed weeks of manual work into routine automated processes

## Key Insights

This transformation highlighted several important lessons:

1. **Technical Debt Is Business Debt**: Neglecting technical maintenance isn't just a developer concern—it directly impacts business growth potential.

2. **Communication Is Critical**: Direct access between product and development teams prevents misunderstandings and ensures alignment.

3. **Stability Before Growth**: Pausing feature development to focus on stability created the foundation needed for accelerated growth later.

4. **User Empathy Drives Success**: Understanding the healthcare professionals' needs led to meaningful improvements that facilitated network growth.

5. **Predictable Processes Build Trust**: Consistent delivery schedules and transparent communication created organizational confidence.

## Conclusion

This case study demonstrates how strategic technical leadership can transform a struggling platform into a growth engine. By prioritizing stability, modernization, and user needs, the revitalized platform not only resolved its technical challenges but enabled unprecedented business growth and new strategic opportunities in the healthcare professional network space.`,
    },
  ];

  return (
    <section
      id="case-studies"
      className={"section " + (darkTheme ? "bg-dark-1" : "bg-light")}
    >
      <div className={"container " + (classicHeader ? "" : "px-lg-5")}>
        {/* Heading */}
        <div className="position-relative d-flex text-center mb-5">
          <h2
            className={
              "text-24  text-uppercase fw-600 w-100 mb-0 " +
              (darkTheme ? "text-white-50 opacity-1" : "text-light opacity-4")
            }
          >
            Success Stories
          </h2>
          <p
            className={
              "text-9 text-dark fw-600 position-absolute w-100 align-self-center lh-base mb-0 " +
              (darkTheme ? "text-white" : "text-dark")
            }
          >
            Case studies
            <span className="heading-separator-line border-bottom border-3 border-primary d-block mx-auto" />
          </p>
        </div>
        {/* Heading end*/}

        <div className="row">
          {caseStudies.map((study) => (
            <div key={study.id} className="col-lg-4 mb-4 d-flex">
              <div
                className={
                  "case-study-card p-4 rounded w-100 d-flex flex-column " +
                  (darkTheme ? "bg-dark-2" : "bg-white") +
                  " shadow"
                }
              >
                <h3 className={darkTheme ? "text-white" : "text-dark"}>
                  {study.title}
                </h3>
                <p className={darkTheme ? "text-white-50" : "text-dark"}>
                  {study.summary}
                </p>

                <div className="mt-auto pt-2">
                  <button
                    className="btn btn-primary"
                    onClick={() =>
                      setExpandedStudy(
                        expandedStudy === study.id ? null : study.id
                      )
                    }
                  >
                    {expandedStudy === study.id
                      ? "Close Case Study"
                      : "Read Full Case Study"}
                  </button>
                </div>

                {expandedStudy === study.id && (
                  <div className="mt-4 case-study-content">
                    <div className={darkTheme ? "text-white-50" : "text-dark"}>
                      <Markdown>{study.content}</Markdown>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
